import settings from '@/settings';

/** What the shopper traded in, as the PDP needs to show it. */
export type TradeInQuoteT = {
  /** Unique per stored trade-in: what Remove matches on. */
  id: string;
  /** The device, e.g. "MacBook Air M1". */
  name: string;
  /** What Reusely will pay for it, in major units of `currency`. */
  value: number;
  currency: string;
  thumbnail?: string;
  /** The condition the shopper graded the device as, e.g. "Good". */
  condition?: string;
  /**
   * The trade-in provider's own reference (Reusely's box id, Nexlife's order
   * ref), issued only once the trade-in order is complete. Absent on a quote
   * the shopper has configured but not submitted.
   */
  boxId?: string;
  /** `committed` came from `checkout`, `quoted` from `view_cart`. */
  status: 'quoted' | 'committed';
  savedAt: string;
};

export const TRADE_IN_QUOTE_KEY = `trade-in-quote-${settings.store}`;

/**
 * Same-tab notification that the stored quote changed. The `storage` event
 * only reaches other tabs, and the widget writes from the same one the banner
 * is rendered in.
 */
export const TRADE_IN_QUOTE_EVENT = 'reebelo:trade-in-quote';

/** One line of the widget's `carts` array. Only the fields we read. */
type WidgetCartItemT = {
  name?: unknown;
  price?: unknown;
  thumbnail?: unknown;
  condition?: unknown;
};

/** The payload a Reusely `checkout` or `view_cart` event hands its listener. */
export type WidgetEventPayloadT = {
  carts?: WidgetCartItemT[];
  total?: unknown;
  currency?: unknown;
  box_id?: unknown;
};

const asText = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;

/** Reusely sends money as a string on some events and a number on others. */
const asAmount = (value: unknown): number | undefined => {
  const amount = typeof value === 'string' ? Number(value) : value;

  return typeof amount === 'number' && Number.isFinite(amount) && amount > 0
    ? amount
    : undefined;
};

const newId = (now: Date) =>
  `${now.getTime()}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Turns a widget event into the quote the page shows, or null when there is
 * nothing worth showing.
 *
 * The documented payloads disagree with their own code samples in places, and
 * the samples do not parse, so nothing here is trusted: a payload that does
 * not carry a named device and a real amount is ignored rather than rendered
 * as a blank or a zero.
 *
 * Several devices can be traded at once. The page shows the first and the
 * event's own `total`, so two devices read as one line at the combined value
 * rather than silently dropping one — see the note in the PDP card.
 */
export const parseWidgetQuote = (
  payload: WidgetEventPayloadT | undefined,
  status: TradeInQuoteT['status'],
  now: Date = new Date(),
): TradeInQuoteT | null => {
  const [first] = payload?.carts ?? [];
  const name = asText(first?.name);
  // `total` covers every device in the trade-in; the line price covers one.
  const value = asAmount(payload?.total) ?? asAmount(first?.price);
  const currency = asText(payload?.currency);

  if (!name || !value || !currency) {
    return null;
  }

  const quote = {
    id: newId(now),
    name,
    value,
    currency,
    ...(asText(first?.thumbnail)
      ? { thumbnail: asText(first?.thumbnail) }
      : {}),
    ...(asText(first?.condition)
      ? { condition: asText(first?.condition) }
      : {}),
    ...(asText(payload?.box_id) ? { boxId: asText(payload?.box_id) } : {}),
    status,
    savedAt: now.toISOString(),
  };

  return quote;
};

/** One device in a Nexlife order summary. Only the fields we read. */
type NexlifeItemT = {
  deviceName?: unknown;
  storageCapacity?: unknown;
  deviceImageUrl?: unknown;
  grade?: unknown;
  offerPrice?: unknown;
  currency?: unknown;
};

/** The `order` of Nexlife's `GET /api/trade-ins/summary` (AU). */
export type NexlifeOrderT = {
  orderRef?: unknown;
  items?: NexlifeItemT[];
};

/**
 * Turns a submitted Nexlife order into the quote the page shows, or null when
 * there is nothing worth showing. Like Reusely's, several devices read as the
 * first one at their combined value.
 */
export const parseNexlifeOrder = (
  order: NexlifeOrderT | undefined,
  now: Date = new Date(),
): TradeInQuoteT | null => {
  const items = order?.items ?? [];
  const [first] = items;
  const name = [asText(first?.deviceName), asText(first?.storageCapacity)]
    .filter(Boolean)
    .join(' ');
  const value = items.reduce(
    (sum, item) => sum + (asAmount(item.offerPrice) ?? 0),
    0,
  );
  const currency = asText(first?.currency);

  if (!asText(first?.deviceName) || !value || !currency) {
    return null;
  }

  return {
    id: newId(now),
    name,
    value,
    currency,
    ...(asText(first?.deviceImageUrl)
      ? { thumbnail: asText(first?.deviceImageUrl) }
      : {}),
    ...(asText(first?.grade) ? { condition: asText(first?.grade) } : {}),
    ...(asText(order?.orderRef) ? { boxId: asText(order?.orderRef) } : {}),
    // Submitted in the widget, as Reusely's `checkout` is.
    status: 'committed',
    savedAt: now.toISOString(),
  };
};

/** One stored trade-in, or null when it isn't one we can show. */
const parseRecord = (record: unknown): TradeInQuoteT | null => {
  if (typeof record !== 'object' || record === null) {
    return null;
  }

  const stored = record as Record<string, unknown>;
  const name = asText(stored.name);
  const value = asAmount(stored.value);
  const currency = asText(stored.currency);
  const savedAt = asText(stored.savedAt);
  // A quote stored before ids existed is the only one, so its timestamp will do.
  const id = asText(stored.id) ?? savedAt;

  if (!name || !value || !currency || !savedAt || !id) {
    return null;
  }

  return {
    id,
    name,
    value,
    currency,
    ...(asText(stored.thumbnail)
      ? { thumbnail: asText(stored.thumbnail) }
      : {}),
    ...(asText(stored.condition)
      ? { condition: asText(stored.condition) }
      : {}),
    ...(asText(stored.boxId) ? { boxId: asText(stored.boxId) } : {}),
    status: stored.status === 'committed' ? 'committed' : 'quoted',
    savedAt,
  };
};

/**
 * Every stored trade-in, oldest first. A single quote, as stored before
 * several were supported, reads as a list of one.
 */
export const parseStoredQuotes = (raw?: string | null): TradeInQuoteT[] => {
  if (!raw) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    return (Array.isArray(parsed) ? parsed : [parsed])
      .map(parseRecord)
      .filter((quote): quote is TradeInQuoteT => quote !== null);
  } catch {
    return [];
  }
};

/**
 * Adds a quote to the list. A completed trade-in replaces the draft and any
 * entry for the same box; a draft replaces the previous draft.
 */
export const addTradeInQuote = (
  quotes: TradeInQuoteT[],
  quote: TradeInQuoteT,
): TradeInQuoteT[] => [
  ...quotes.filter(
    (q) =>
      q.status === 'committed' && (!quote.boxId || q.boxId !== quote.boxId),
  ),
  quote,
];

export const readTradeInQuotes = (): TradeInQuoteT[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    return parseStoredQuotes(localStorage.getItem(TRADE_IN_QUOTE_KEY));
  } catch (err) {
    // Storage can be blocked outright (private mode, blocked site data).
    return [];
  }
};

const writeTradeInQuotes = (quotes: TradeInQuoteT[]): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (quotes.length > 0) {
      localStorage.setItem(TRADE_IN_QUOTE_KEY, JSON.stringify(quotes));
    } else {
      localStorage.removeItem(TRADE_IN_QUOTE_KEY);
    }

    window.dispatchEvent(new Event(TRADE_IN_QUOTE_EVENT));
  } catch (err) {
    // Nothing to show on the next render; the widget still took the trade-in.
  }
};

export const saveTradeInQuote = (quote: TradeInQuoteT): void => {
  writeTradeInQuotes(addTradeInQuote(readTradeInQuotes(), quote));
};

/** Removes one trade-in from this browser only; Reusely isn't told. */
export const removeTradeInQuote = (quote: TradeInQuoteT): void => {
  writeTradeInQuotes(readTradeInQuotes().filter((q) => q.id !== quote.id));
};

export const clearTradeInQuote = (): void => {
  writeTradeInQuotes([]);
};
